/**
 * Pure attendance statistics (F3): time series per week/month, average and
 * growth — over the attendance sessions sync already authorized. No DB, no DOM:
 * unit-testable.
 */

export interface AttendanceSessionLike {
  id: string;
  headcount: number | null;
  occurrence_start: string | null;
  church_id?: string | null;
  group_id?: string | null;
  meeting_id?: string | null;
}

export type StatsGranularity = "week" | "month";

export interface AttendancePoint {
  /** `YYYY-MM` or `YYYY-Www` (ISO, UTC). */
  period: string;
  sessions: number;
  total: number;
  average: number;
}

export interface AttendanceOverview {
  sessions: number;
  total: number;
  /** Average attendees per session (0 if there are no sessions). */
  average: number;
  /**
   * Growth % of the last period vs the previous one (over the period TOTAL).
   * `null` if there are not two comparable periods or the previous one is 0.
   */
  growthPct: number | null;
  series: AttendancePoint[];
}

export function parseIsoMs(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const ms = Date.parse(value.includes("T") ? value : `${value.replace(" ", "T")}Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** `YYYY-MM` UTC. */
export function monthPeriod(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** ISO-8601 week (`YYYY-Www`), computed in UTC. */
export function isoWeekPeriod(ms: number): string {
  const d = new Date(ms);
  // Thursday of the date's ISO week decides the ISO year.
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayOfWeek = target.getUTCDay() || 7; // lunes=1 … domingo=7
  target.setUTCDate(target.getUTCDate() + 4 - dayOfWeek);
  const isoYear = target.getUTCFullYear();
  const yearStart = Date.UTC(isoYear, 0, 1);
  const week = Math.ceil(((target.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

export interface AttendanceFilter {
  churchId?: string | null;
  groupId?: string | null;
  meetingId?: string | null;
  fromMs?: number | null;
  toMs?: number | null;
}

/** Series per period (chronological order) of the filtered sessions. */
export function attendanceSeries(
  sessions: readonly AttendanceSessionLike[],
  granularity: StatsGranularity = "month",
  filter: AttendanceFilter = {},
): AttendancePoint[] {
  const buckets = new Map<string, { sessions: number; total: number }>();
  for (const s of sessions) {
    if (filter.churchId && s.church_id !== filter.churchId) {
      continue;
    }
    if (filter.groupId && s.group_id !== filter.groupId) {
      continue;
    }
    if (filter.meetingId && s.meeting_id !== filter.meetingId) {
      continue;
    }
    const ms = parseIsoMs(s.occurrence_start);
    if (ms === null) {
      continue;
    }
    if (filter.fromMs != null && ms < filter.fromMs) {
      continue;
    }
    if (filter.toMs != null && ms > filter.toMs) {
      continue;
    }
    const period = granularity === "week" ? isoWeekPeriod(ms) : monthPeriod(ms);
    const bucket = buckets.get(period) ?? { sessions: 0, total: 0 };
    bucket.sessions += 1;
    bucket.total += s.headcount ?? 0;
    buckets.set(period, bucket);
  }
  return [...buckets.entries()]
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([period, b]) => ({
      period,
      sessions: b.sessions,
      total: b.total,
      average: b.sessions > 0 ? b.total / b.sessions : 0,
    }));
}

/** Totals + trend (last period vs previous) of a series. */
export function attendanceOverview(series: readonly AttendancePoint[]): AttendanceOverview {
  const sessions = series.reduce((sum, p) => sum + p.sessions, 0);
  const total = series.reduce((sum, p) => sum + p.total, 0);
  let growthPct: number | null = null;
  if (series.length >= 2) {
    const previous = series.at(-2)!.total;
    const last = series.at(-1)!.total;
    if (previous > 0) {
      growthPct = ((last - previous) / previous) * 100;
    }
  }
  return {
    sessions,
    total,
    average: sessions > 0 ? total / sessions : 0,
    growthPct,
    series: [...series],
  };
}
